import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, History, Calculator, Square, Copy, Check } from 'lucide-react';

interface HistoryEntry {
  id: number;
  from: string;
  to: string;
  timestamp: string;
}

interface UnitDefinition {
  factor: number;
  category: string;
  symbol: string;
  fullName: string;
  description: string;
}

type UnitName = 'square_millimeters' | 'square_centimeters' | 'square_decimeters' | 'square_meters' | 'square_kilometers' | 'hectares' | 'ares' | 'square_inches' | 'square_feet' | 'square_yards' | 'square_miles' | 'acres' | 'square_rods' | 'roods' | 'perches' | 'square_chains' | 'townships' | 'sections' | 'barns' | 'circular_mils' | 'square_mils' | 'dunams' | 'tatami' | 'tsubo' | 'cho' | 'ping' | 'rai' | 'ngaan' | 'wah';

const AreaConverter: React.FC = () => {
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');
  const [fromUnit, setFromUnit] = useState<UnitName>('square_meters');
  const [toUnit, setToUnit] = useState<UnitName>('square_feet');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [precision, setPrecision] = useState<number>(4);

  // Comprehensive area unit definitions with conversion factors to square meters
  const units: Record<UnitName, UnitDefinition> = {
    // Metric
    'square_millimeters': { 
      factor: 1e-6, 
      category: 'Metric', 
      symbol: 'mm²', 
      fullName: 'Square Millimeters',
      description: 'Very small areas, technical drawings'
    },
    'square_centimeters': { 
      factor: 1e-4, 
      category: 'Metric', 
      symbol: 'cm²', 
      fullName: 'Square Centimeters',
      description: 'Small areas, body surface area'
    },
    'square_decimeters': { 
      factor: 0.01, 
      category: 'Metric', 
      symbol: 'dm²', 
      fullName: 'Square Decimeters',
      description: 'Medium areas, book pages'
    },
    'square_meters': { 
      factor: 1, 
      category: 'Metric', 
      symbol: 'm²', 
      fullName: 'Square Meters',
      description: 'Standard metric area unit'
    },
    'square_kilometers': { 
      factor: 1000000, 
      category: 'Metric', 
      symbol: 'km²', 
      fullName: 'Square Kilometers',
      description: 'Large areas, cities, countries'
    },
    'hectares': { 
      factor: 10000, 
      category: 'Metric', 
      symbol: 'ha', 
      fullName: 'Hectares',
      description: 'Agricultural land, parks'
    },
    'ares': { 
      factor: 100, 
      category: 'Metric', 
      symbol: 'a', 
      fullName: 'Ares',
      description: 'Small agricultural plots'
    },
    
    // Imperial/US
    'square_inches': { 
      factor: 0.00064516, 
      category: 'Imperial', 
      symbol: 'sq in', 
      fullName: 'Square Inches',
      description: 'Small areas, screen sizes'
    },
    'square_feet': { 
      factor: 0.092903, 
      category: 'Imperial', 
      symbol: 'sq ft', 
      fullName: 'Square Feet',
      description: 'Room sizes, floor areas'
    },
    'square_yards': { 
      factor: 0.836127, 
      category: 'Imperial', 
      symbol: 'sq yd', 
      fullName: 'Square Yards',
      description: 'Carpeting, fabric'
    },
    'square_miles': { 
      factor: 2589988.11, 
      category: 'Imperial', 
      symbol: 'sq mi', 
      fullName: 'Square Miles',
      description: 'Large areas, states, countries'
    },
    'acres': { 
      factor: 4046.86, 
      category: 'Imperial', 
      symbol: 'ac', 
      fullName: 'Acres',
      description: 'Agricultural land, real estate'
    },
    
    // Surveying
    'square_rods': { 
      factor: 25.2929, 
      category: 'Surveying', 
      symbol: 'sq rd', 
      fullName: 'Square Rods',
      description: 'Historical surveying unit'
    },
    'roods': { 
      factor: 1011.71, 
      category: 'Surveying', 
      symbol: 'ro', 
      fullName: 'Roods',
      description: '1/4 acre, British surveying'
    },
    'perches': { 
      factor: 25.2929, 
      category: 'Surveying', 
      symbol: 'per', 
      fullName: 'Perches',
      description: 'Same as square rod'
    },
    'square_chains': { 
      factor: 404.686, 
      category: 'Surveying', 
      symbol: 'sq ch', 
      fullName: 'Square Chains',
      description: 'Surveying, 1/10 acre'
    },
    'townships': { 
      factor: 93239571.97, 
      category: 'Surveying', 
      symbol: 'twp', 
      fullName: 'Townships',
      description: 'US land surveying, 36 sq mi'
    },
    'sections': { 
      factor: 2589988.11, 
      category: 'Surveying', 
      symbol: 'sec', 
      fullName: 'Sections',
      description: 'US land surveying, 1 sq mi'
    },
    
    // Scientific
    'barns': { 
      factor: 1e-28, 
      category: 'Scientific', 
      symbol: 'b', 
      fullName: 'Barns',
      description: 'Nuclear cross-sections'
    },
    'circular_mils': { 
      factor: 5.067075e-10, 
      category: 'Scientific', 
      symbol: 'cmil', 
      fullName: 'Circular Mils',
      description: 'Wire cross-sectional area'
    },
    'square_mils': { 
      factor: 6.4516e-10, 
      category: 'Scientific', 
      symbol: 'sq mil', 
      fullName: 'Square Mils',
      description: 'Thin films, coatings'
    },
    
    // Regional
    'dunams': { 
      factor: 1000, 
      category: 'Regional', 
      symbol: 'dunam', 
      fullName: 'Dunams',
      description: 'Middle Eastern land measurement'
    },
    'tatami': { 
      factor: 1.65289, 
      category: 'Regional', 
      symbol: '畳', 
      fullName: 'Tatami',
      description: 'Japanese room measurement'
    },
    'tsubo': { 
      factor: 3.30579, 
      category: 'Regional', 
      symbol: '坪', 
      fullName: 'Tsubo',
      description: 'Japanese area, 2 tatami'
    },
    'cho': { 
      factor: 9917.36, 
      category: 'Regional', 
      symbol: '町', 
      fullName: 'Cho',
      description: 'Japanese large area'
    },
    'ping': { 
      factor: 3.30579, 
      category: 'Regional', 
      symbol: '坪', 
      fullName: 'Ping',
      description: 'Taiwan/Chinese area unit'
    },
    'rai': { 
      factor: 1600, 
      category: 'Regional', 
      symbol: 'ไร่', 
      fullName: 'Rai',
      description: 'Thai land measurement'
    },
    'ngaan': { 
      factor: 400, 
      category: 'Regional', 
      symbol: 'งาน', 
      fullName: 'Ngaan',
      description: 'Thai area, 1/4 rai'
    },
    'wah': { 
      factor: 4, 
      category: 'Regional', 
      symbol: 'วา', 
      fullName: 'Wah',
      description: 'Thai small area unit'
    }
  };

  const categories = Array.from(new Set(Object.values(units).map(unit => unit.category)));

  const convert = (value: number, from: UnitName, to: UnitName): number => {
    if (!value && value !== 0 || isNaN(value)) return 0;
    const fromFactor = units[from].factor;
    const toFactor = units[to].factor;
    const result = (value * fromFactor) / toFactor;
    return parseFloat(result.toPrecision(precision));
  };

  const handleFromValueChange = (value: string): void => {
    setFromValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), fromUnit, toUnit);
      setToValue(converted.toString());
    } else {
      setToValue('');
    }
  };

  const handleToValueChange = (value: string): void => {
    setToValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), toUnit, fromUnit);
      setFromValue(converted.toString());
    } else {
      setFromValue('');
    }
  };

  const swapUnits = (): void => {
    const tempUnit = fromUnit;
    const tempValue = fromValue;
    
    setFromUnit(toUnit);
    setToUnit(tempUnit);
    setFromValue(toValue);
    setToValue(tempValue);
  };

  const addToHistory = (): void => {
    if ((fromValue || fromValue === '0') && (toValue || toValue === '0')) {
      const entry: HistoryEntry = {
        id: Date.now(),
        from: `${fromValue} ${units[fromUnit].symbol}`,
        to: `${toValue} ${units[toUnit].symbol}`,
        timestamp: new Date().toLocaleTimeString()
      };
      setHistory(prev => [entry, ...prev.slice(0, 9)]); // Keep last 10 entries
    }
  };

  const copyResult = (): void => {
    if (toValue || toValue === '0') {
      navigator.clipboard.writeText(`${toValue} ${units[toUnit].symbol}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = (): void => {
    setFromValue('');
    setToValue('');
  };

  const getAreaVisualization = (area: number, unit: UnitName): string[] => {
    const squareMeters = area * units[unit].factor;
    const visualizations: string[] = [];
    
    if (squareMeters < 1) visualizations.push('Smaller than a sheet of paper');
    else if (squareMeters < 10) visualizations.push('Size of a large table');
    else if (squareMeters < 50) visualizations.push('Small room size');
    else if (squareMeters < 100) visualizations.push('Large room or small apartment');
    else if (squareMeters < 500) visualizations.push('Small house');
    else if (squareMeters < 2000) visualizations.push('Large house');
    else if (squareMeters < 10000) visualizations.push('Small commercial building');
    else if (squareMeters < 100000) visualizations.push('Large building or city block');
    else if (squareMeters < 1000000) visualizations.push('Small neighborhood');
    else visualizations.push('Large area or district');
    
    // Sports field comparisons
    if (Math.abs(squareMeters - 7140) < 500) visualizations.push('About the size of a football field');
    if (Math.abs(squareMeters - 2808) < 200) visualizations.push('About the size of a tennis court');
    if (Math.abs(squareMeters - 420) < 50) visualizations.push('About the size of a basketball court');
    
    return visualizations;
  };

  const getCommonEquivalents = (area: number, unit: UnitName): string[] => {
    const squareMeters = area * units[unit].factor;
    const equivalents: string[] = [];
    
    // Common conversions
    if (unit !== 'square_feet' && squareMeters > 0.01) {
      const sqFt = squareMeters / units.square_feet.factor;
      equivalents.push(`≈ ${sqFt.toFixed(1)} sq ft`);
    }
    
    if (unit !== 'acres' && squareMeters > 1000) {
      const acres = squareMeters / units.acres.factor;
      equivalents.push(`≈ ${acres.toFixed(2)} acres`);
    }
    
    if (unit !== 'hectares' && squareMeters > 5000) {
      const hectares = squareMeters / units.hectares.factor;
      equivalents.push(`≈ ${hectares.toFixed(2)} hectares`);
    }
    
    return equivalents.slice(0, 2); // Limit to 2 equivalents
  };

  useEffect(() => {
    if ((fromValue || fromValue === '0') && !isNaN(Number(fromValue))) {
      const converted = convert(parseFloat(fromValue), fromUnit, toUnit);
      setToValue(converted.toString());
    }
  }, [fromUnit, toUnit, precision, fromValue]);

  const renderUnitOptions = (): React.ReactElement[] => {
    return categories.map(category => (
      <optgroup key={category} label={category}>
        {Object.entries(units)
          .filter(([_, unit]) => unit.category === category)
          .map(([unitName, unit]) => (
            <option key={unitName} value={unitName}>
              {unit.fullName} ({unit.symbol})
            </option>
          ))}
      </optgroup>
    ));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-green-50 to-emerald-100 min-h-screen">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Square className="w-8 h-8 text-green-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Advanced Area Converter</h1>
          </div>
          <p className="text-gray-600">Convert between multiple area units with precision and visualizations</p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-6 justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Precision:</label>
            <select 
              value={precision} 
              onChange={(e) => setPrecision(parseInt(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {[2, 3, 4, 5, 6, 8, 10].map(p => (
                <option key={p} value={p}>{p} digits</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <History className="w-4 h-4" />
            History
          </button>
          
          <button
            onClick={clearAll}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            Clear All
          </button>
        </div>

        {/* Main Converter */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* From Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              From
            </h3>
            <div className="space-y-3">
              <input
                type="number"
                value={fromValue}
                onChange={(e) => handleFromValueChange(e.target.value)}
                placeholder="Enter area"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[fromUnit].description}
              </div>
            </div>
          </div>

          {/* To Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              To
            </h3>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="number"
                  value={toValue}
                  onChange={(e) => handleToValueChange(e.target.value)}
                  placeholder="Result"
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={copyResult}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Copy result"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                </button>
              </div>
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[toUnit].description}
              </div>
            </div>
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={swapUnits}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-md"
          >
            <ArrowLeftRight className="w-5 h-5" />
            Swap Units
          </button>
        </div>

        {/* Add to History Button */}
        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="flex justify-center mb-6">
            <button
              onClick={addToHistory}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Add to History
            </button>
          </div>
        )}

        {/* Conversion Summary */}
        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Conversion Summary</h4>
            <p className="text-lg mb-3">
              <span className="font-mono bg-white px-2 py-1 rounded">{fromValue} {units[fromUnit].symbol}</span>
              <span className="mx-3 text-gray-500">=</span>
              <span className="font-mono bg-white px-2 py-1 rounded font-semibold text-green-700">{toValue} {units[toUnit].symbol}</span>
            </p>
            
            {/* Area Visualization */}
            {fromValue && !isNaN(Number(fromValue)) && (
              <div className="space-y-2">
                <div className="text-sm text-gray-600">
                  <strong>Size Comparison:</strong>
                  {getAreaVisualization(parseFloat(fromValue), fromUnit).map(viz => (
                    <span key={viz} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                      {viz}
                    </span>
                  ))}
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Common Equivalents:</strong>
                  {getCommonEquivalents(parseFloat(fromValue), fromUnit).map(equiv => (
                    <span key={equiv} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                      {equiv}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Reference */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Common Area References</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div className="space-y-1">
              <h5 className="font-medium text-blue-800">Sports Fields</h5>
              <div>Football field: 7,140 m²</div>
              <div>Tennis court: 260.9 m²</div>
              <div>Basketball court: 420 m²</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-green-800">Real Estate</h5>
              <div>Small house: 100-200 m²</div>
              <div>Large house: 300-500 m²</div>
              <div>1 acre: 4,047 m²</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-purple-800">Common Areas</h5>
              <div>Parking space: 12.5 m²</div>
              <div>Office cubicle: 5-8 m²</div>
              <div>City block: ~40,000 m²</div>
            </div>
          </div>
        </div>

        {/* History */}
        {showHistory && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <History className="w-5 h-5" />
              Conversion History
            </h4>
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No conversions in history</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((entry) => (
                  <div key={entry.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{entry.from}</span>
                      <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-sm font-semibold">{entry.to}</span>
                    </div>
                    <span className="text-xs text-gray-500">{entry.timestamp}</span>
                  </div>
                ))}
              </div>
            )}
            {history.length > 0 && (
              <button
                onClick={() => setHistory([])}
                className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm"
              >
                Clear History
              </button>
            )}
          </div>
        )}

        {/* Unit Categories Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(category => (
            <div key={category} className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-semibold text-gray-800 mb-2">{category}</h5>
              <div className="space-y-1">
                {Object.entries(units)
                  .filter(([_, unit]) => unit.category === category)
                  .slice(0, 5)
                  .map(([unitName, unit]) => (
                    <div key={unitName} className="text-sm text-gray-600">
                      {unit.symbol} - {unit.fullName}
                    </div>
                  ))}
                {Object.entries(units).filter(([_, unit]) => unit.category === category).length > 5 && (
                  <div className="text-xs text-gray-500">+ more...</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AreaConverter;